import { DropdownMenu, Icon, IconName } from '@usealloy/component-library';
import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Collapsible } from '../component/Collapsible';
import { Sidebar } from '../component/Sidebar';
import {
  isApplicationQueueActive,
  isInvestigationsActive,
  isPrefixActive,
} from './routeMatchers';
import {
  CollapsibleTriggerIcon,
  useCollapsibleNavItem,
} from './useCollapsibleNavItem';

// ============================================================
//     INTERNAL COMPONENTS
// ============================================================
interface ReportsNavItemProps {
  showCtrsLink: boolean;
  showSarsLink: boolean;
}

/**
 * The `Reports` nav item — SARs and CTRs.
 *
 * Renders a collapsible submenu when the sidebar is expanded, otherwise a
 * right-side dropdown (FE2.0 `ReportsCollapsibleNavItem`).
 */
const ReportsNavItem = (props: ReportsNavItemProps) => {
  const { showCtrsLink, showSarsLink } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isSarsActive = isPrefixActive(routes.sars.list, pathname);
  const isCtrsActive = isPrefixActive(routes.v3.dashboard.ctrs.list, pathname);
  const isSubRouteActive = (showSarsLink && isSarsActive) || (showCtrsLink && isCtrsActive);

  const {
    isCollapsibleOpen,
    isDropdownOpen,
    isExpanded,
    isParentActive,
    setIsDropdownOpen,
    toggleCollapsible,
  } = useCollapsibleNavItem('SideNav.Reports.isOpen', isSubRouteActive);

  // Collapsed rail: the item becomes a right-side dropdown.
  // (No tooltip on the trigger — the component-library Tooltip and DropdownMenu
  // trigger wrappers can't be nested on the same button.)
  if (!isExpanded) {
    return (
      <Sidebar.MenuItem>
        <DropdownMenu
          align="start"
          isOpen={isDropdownOpen}
          onOpenChange={setIsDropdownOpen}
          side="right"
          trigger={(
            <Sidebar.MenuButton
              data-dd-action-name="alloydd-sidebar-nav-reports"
              isActive={isParentActive}
              tooltip="Reports"
            >
              <Icon icon={IconName.Files} size="large" />
              <span className="fs-unmask">Reports</span>
            </Sidebar.MenuButton>
          )}
        >
          {showSarsLink && (
            <DropdownMenu.Item
              data-dd-action-name="alloydd-sidebar-nav-sars"
              onSelect={() => navigate(routes.sars.list)}
            >
              SARs
            </DropdownMenu.Item>
          )}
          {showCtrsLink && (
            <DropdownMenu.Item
              data-dd-action-name="alloydd-sidebar-nav-ctrs"
              onSelect={() => navigate(routes.v3.dashboard.ctrs.list)}
            >
              CTRs
            </DropdownMenu.Item>
          )}
        </DropdownMenu>
      </Sidebar.MenuItem>
    );
  }

  return (
    <Sidebar.MenuItem>
      <Sidebar.MenuButton
        data-dd-action-name="alloydd-sidebar-nav-reports"
        isActive={isParentActive}
        onClick={toggleCollapsible}
      >
        <CollapsibleTriggerIcon isOpen={isCollapsibleOpen} />
        <span className="fs-unmask">Reports</span>
      </Sidebar.MenuButton>
      <Collapsible open={isCollapsibleOpen}>
        <Sidebar.Menu isSubmenu>
          {showSarsLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as={Link}
                data-dd-action-name="alloydd-sidebar-nav-sars"
                isActive={isSarsActive}
                to={routes.sars.list}
              >
                <span className="fs-unmask">SARs</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}
          {showCtrsLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as={Link}
                data-dd-action-name="alloydd-sidebar-nav-ctrs"
                isActive={isCtrsActive}
                to={routes.v3.dashboard.ctrs.list}
              >
                <span className="fs-unmask">CTRs</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}
        </Sidebar.Menu>
      </Collapsible>
    </Sidebar.MenuItem>
  );
};

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * "Review" nav group — Applications, Investigations, Case Management,
 * Review Queue, and the Reports collapsible (SARs, CTRs).
 *
 * Mirrors the FE2.0 SideNav `NavList.review` structure and labels. Conditions,
 * link types (full-page `<a>` vs SPA `<Link>`), and Datadog action names are
 * ported verbatim from the legacy sidebar
 * (`~/dashboard/components/Sidebar/Sidebar.jsx`).
 */
export const ReviewGroup = () => {
  const {
    agent, featureFlags, permissions, reviewQueueEnabled,
  } = useContext(UserContext);
  const { pathname } = useLocation();

  const showJourneysLink = featureFlags?.journeys && permissions?.['READ:Journey:journey_id'];
  const showAppQueueLink = showJourneysLink && permissions?.['READ:JourneyApplications:null'];
  const showInvestigationsLink = featureFlags?.investigations && permissions?.['READ:Investigations:null'];
  const showReviewQueueLink = permissions?.['READ:Entities:null']
    && reviewQueueEnabled
    && (featureFlags?.journeys ? !featureFlags?.hideReviewQueue : true);
  const showCaseManagementLink = permissions?.['READ:Entities:null'] && featureFlags?.caseManagement;
  const showSarsLink = featureFlags?.sarReport
    && (featureFlags?.reportingPermissions
      ? permissions?.['READ:SarReports:sar_report_id']
      : permissions?.['READ:SarReports:null'])
    && !agent?.isEmployee;
  const showCtrsLink = featureFlags?.currencyTransactionReports
    && permissions?.['READ:CtrReports:null'];

  const showReports = showSarsLink || showCtrsLink;

  const hasVisibleItem = [
    showAppQueueLink,
    showInvestigationsLink,
    showCaseManagementLink,
    showReviewQueueLink,
    showReports,
  ].some(Boolean);

  if (!hasVisibleItem) return null;

  return (
    <Sidebar.Group>
      <Sidebar.GroupLabel>
        <span className="fs-unmask">Review</span>
      </Sidebar.GroupLabel>
      <Sidebar.Menu>
        {showAppQueueLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-application-queue"
              href={`${window.location.origin}/beta/journeys/applications`}
              isActive={isApplicationQueueActive(
                routes.v3.dashboard.journeys.list,
                pathname,
              )}
              tooltip="Applications"
            >
              <Icon icon={IconName.ApplicationReview} size="large" />
              <span className="fs-unmask">Applications</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showInvestigationsLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as={Link}
              data-dd-action-name="alloydd-sidebar-nav-investigations"
              isActive={isInvestigationsActive(
                routes.investigations.queue,
                pathname,
              )}
              to={routes.investigations.queue}
              tooltip="Investigations"
            >
              <Icon icon={IconName.FolderOpenInvestigation} size="large" />
              <span className="fs-unmask">Investigations</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showCaseManagementLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-case-management"
              href={`${window.location.origin}${routes.v3.dashboard.cases.list}`}
              isActive={isPrefixActive(
                routes.v3.dashboard.cases.list,
                pathname,
              )}
              tooltip="Case Management"
            >
              <Icon icon={IconName.CaseManagement} size="large" />
              <span className="fs-unmask">Case Management</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showReviewQueueLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-review-queue"
              href={`${window.location.origin}${routes.v3.dashboard.queues.list}`}
              isActive={isPrefixActive(
                routes.v3.dashboard.queues.list,
                pathname,
              )}
              tooltip="Review Queue"
            >
              <Icon icon={IconName.BallotCheck} size="large" />
              <span className="fs-unmask">Review Queue</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showReports && (
          <ReportsNavItem
            showCtrsLink={Boolean(showCtrsLink)}
            showSarsLink={Boolean(showSarsLink)}
          />
        )}
      </Sidebar.Menu>
    </Sidebar.Group>
  );
};
